import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Paper,
  Button,
  CardActions,
  Box
} from "@mui/material";
import { Code as CodeIcon, ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import projects from "../data/projects"; 

export const ProjectsSection = ({ limit = 6 }) => {
  const navigate = useNavigate();
  const displayProjects = limit ? projects.slice(0, limit) : projects;

  const truncateDescription = (text, maxLength = 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  return (
    <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, display: "flex", alignItems: "center" }}>
          <CodeIcon sx={{ mr: 1.5, fontSize: 32, color: "#1976d2" }} />
          Featured Projects
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Explore my recent work and technical projects
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {displayProjects.map((project, idx) => (
          <Grid item xs={12} sm={6} md={4} key={project.id}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                border: "1px solid #e0e0e0",
                borderRadius: 3,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                  transform: "scaleX(0)",
                  transformOrigin: "left",
                  transition: "transform 0.3s ease"
                },
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 12px 24px rgba(25, 118, 210, 0.15)",
                  borderColor: "#1976d2",
                  "&::before": {
                    transform: "scaleX(1)"
                  }
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}>
                  <Chip 
                    icon={<CodeIcon sx={{ fontSize: 14 }} />}
                    label={project.role || "Developer"} 
                    size="small" 
                    sx={{ 
                      bgcolor: "#e3f2fd", 
                      color: "#1565c0",
                      fontWeight: 600,
                      fontSize: "0.75rem"
                    }}
                  />
                  {project.status && (
                    <Chip
                      label={project.status}
                      size="small"
                      sx={{ 
                        bgcolor: project.status === "In Progress" ? "#fff3e0" : "#e8f5e9",
                        color: project.status === "In Progress" ? "#e65100" : "#2e7d32",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }}
                    />
                  )}
                </Stack>

                <Typography 
                  variant="h6" 
                  fontWeight="700" 
                  sx={{ 
                    fontSize: "1.1rem", 
                    mb: 1,
                    color: "#1a1a1a",
                    lineHeight: 1.3
                  }}
                >
                  {project.title}
                </Typography>

                {project.institution && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: "block",
                      mb: 1.5,
                      color: "#666",
                      textTransform: "uppercase", 
                      letterSpacing: 1,
                      fontWeight: 600,
                      fontSize: "0.7rem"
                    }}
                  >
                    {project.institution}
                  </Typography>
                )}

                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 2.5, 
                    color: "#555",
                    lineHeight: 1.6,
                    minHeight: "66px"
                  }}
                >
                  {truncateDescription(project.description, 100)}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: "block", 
                      mb: 1, 
                      color: "#888",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.5
                    }}
                  >
                    Tech Stack
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                    {project.techStack.slice(0, 3).map((tech, i) => (
                      <Chip 
                        key={i} 
                        label={tech} 
                        size="small" 
                        sx={{ 
                          fontSize: "0.7rem",
                          height: "24px",
                          bgcolor: "#f5f5f5",
                          border: "1px solid #e0e0e0",
                          color: "#555",
                          fontWeight: 500,
                          "&:hover": {
                            bgcolor: "#eeeeee"
                          }
                        }} 
                      />
                    ))}
                    {project.techStack.length > 3 && (
                      <Chip 
                        label={`+${project.techStack.length - 3} more`} 
                        size="small" 
                        sx={{ 
                          fontSize: "0.7rem",
                          height: "24px",
                          bgcolor: "#e3f2fd",
                          border: "1px solid #90caf9",
                          color: "#1565c0",
                          fontWeight: 600
                        }} 
                      />
                    )}
                  </Stack>
                </Box>
              </CardContent>

              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    py: 1.2,
                    fontSize: "0.9rem",
                    bgcolor: "#1976d2",
                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.25)",
                    "&:hover": {
                      bgcolor: "#1565c0",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.35)",
                      transform: "translateY(-2px)"
                    },
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  View Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {limit && projects.length > limit && (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Button
            variant="outlined"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/projects")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 5,
              py: 1.5,
              borderRadius: 2,
              borderWidth: 2,
              borderColor: "#1976d2",
              color: "#1976d2",
              fontSize: "1rem",
              "&:hover": {
                borderWidth: 2,
                borderColor: "#1565c0",
                bgcolor: "rgba(25, 118, 210, 0.08)",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)"
              },
              transition: "all 0.2s ease"
            }}
          >
            View All Projects
          </Button>
        </Box>
      )}
    </Paper>
  );
};